import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { CheckCircle2, Truck } from "lucide-react";
import { api } from "../../lib/api";

export function PublicDeliveryNotePage() {
  const { token } = useParams<{ token: string }>();
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [idCardNumber, setIdCardNumber] = useState("");
  const [idCardIssuePlace, setIdCardIssuePlace] = useState("");
  const [confirmNote, setConfirmNote] = useState("");

  const { data: note, isLoading, refetch } = useQuery({
    queryKey: ["public-delivery-note", token],
    queryFn: () => api.getPublicDeliveryNote(token!),
    enabled: !!token,
  });

  const confirm = useMutation({
    mutationFn: () =>
      api.confirmPublicDeliveryNote(token!, {
        confirmedByName: name,
        confirmedBySurname: surname,
        confirmedByIdCardNumber: idCardNumber,
        confirmedByIdCardIssuePlace: idCardIssuePlace,
        confirmedNote: confirmNote.trim() || undefined,
      }),
    onSuccess: () => refetch(),
  });

  if (isLoading || !note) {
    return <p className="p-8 text-center text-sm text-slate-500">Učitavanje...</p>;
  }

  const isConfirmed = note.status === "CONFIRMED";
  const canSubmit = name.trim() && surname.trim() && idCardNumber.length === 9 && idCardIssuePlace.trim();

  return (
    <div className="mx-auto max-w-lg space-y-5 p-6">
      <div className="flex items-center gap-2">
        <Truck className="h-5 w-5 text-brand" aria-hidden />
        <h1 className="text-lg font-semibold text-slate-900">
          Otpremnica {note.documentNumber ?? ""}
        </h1>
      </div>
      <p className="text-sm text-slate-500">
        {note.companyName} → {note.customerName ?? "Kupac"} ·{" "}
        {new Date(note.issueDate).toLocaleDateString("sr-RS")}
      </p>

      <div className="rounded-xl border border-border bg-surface p-4">
        <h2 className="mb-2 text-sm font-semibold text-slate-800">Stavke za isporuku</h2>
        <table>
          <thead>
            <tr>
              <th>Naziv</th>
              <th>Količina</th>
            </tr>
          </thead>
          <tbody>
            {note.items.map((item, i) => (
              <tr key={i}>
                <td>
                  {item.name}
                  {item.serialNumber && (
                    <div className="text-xs text-slate-400">Serijski broj: {item.serialNumber}</div>
                  )}
                </td>
                <td>
                  {item.quantity} {item.unit}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isConfirmed ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
            <CheckCircle2 className="h-5 w-5" aria-hidden />
            <div>
              <p>Prijem robe je potvrđen.</p>
              {note.confirmedAt && (
                <p className="text-xs text-emerald-700">
                  {note.confirmedByName} {note.confirmedBySurname} ·{" "}
                  {new Date(note.confirmedAt).toLocaleString("sr-RS")}
                </p>
              )}
            </div>
          </div>
          <a
            href={api.getPublicDeliveryNotePdfUrl(token!)}
            className="block w-full rounded-lg border border-border bg-surface py-2.5 text-center text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Preuzmi otpremnicu (PDF)
          </a>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-surface p-4">
          <h2 className="mb-3 text-sm font-semibold text-slate-800">Potvrda prijema robe</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Ime</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Prezime</label>
              <input
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Broj lične karte</label>
              <input
                value={idCardNumber}
                onChange={(e) => setIdCardNumber(e.target.value.replace(/\D/g, "").slice(0, 9))}
                inputMode="numeric"
                maxLength={9}
                placeholder="9 cifara"
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Mesto izdavanja</label>
              <input
                value={idCardIssuePlace}
                onChange={(e) => setIdCardIssuePlace(e.target.value)}
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>

          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-slate-500">
              Napomena (opciono) - ako nešto nije u redu ili imate bilo kakvu primedbu
            </label>
            <textarea
              value={confirmNote}
              onChange={(e) => setConfirmNote(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>

          {confirm.isError && (
            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              Potvrda nije uspela. Proverite unete podatke.
            </p>
          )}

          <button
            disabled={!canSubmit || confirm.isPending}
            onClick={() => confirm.mutate()}
            className="mt-4 w-full rounded-lg bg-brand py-2.5 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50"
          >
            {confirm.isPending ? "Slanje..." : "Potvrdi prijem robe"}
          </button>
        </div>
      )}
    </div>
  );
}
